<script setup lang="ts">
    import { useElementSize } from '@vueuse/core';
    import { useCollapsibleSection } from '~/composables/useCollapsibleSection';

    const props = defineProps<{ heightVar?: string }>();

    const { isOpen, contentRef, contentHeight, toggle } = useCollapsibleSection(false);
    const bannerRoot = ref<HTMLElement | null>(null);
    const { height } = useElementSize(bannerRoot);

    watchEffect(() => {
        if (!props.heightVar || height.value === 0) return;
        document.documentElement.style.setProperty(props.heightVar, `${height.value}px`);
    });

    onBeforeUnmount(() => {
        if (props.heightVar) {
            document.documentElement.style.removeProperty(props.heightVar);
        }
    });
</script>

<template>
    <div
        ref="bannerRoot"
        class="w-full"
    >
        <div class="hidden md:flex items-center gap-4 px-8 py-2.5 lg:gap-8 w-full h-[60px]">
            <div class="flex-1 flex flex-col justify-center min-w-0">
                <slot name="title" />
                <slot name="body" />
            </div>
            <slot name="action" />
        </div>

        <div class="md:hidden">
            <!-- 44px rather than the desktop row's 60: on a phone this collapsed header
                 is pure chrome above the page, and every pixel it takes is one the
                 content loses. 44px is the floor, not an arbitrary trim — it is the
                 minimum tap target `.claude/rules/responsive.md` asks for, and this
                 whole row is the toggle. The layout follows on its own: the banner
                 wrapper is measured into `--demo-banner-height`, which drives the
                 mobile header offset and `main`'s height in `layouts/base.vue`. -->
            <button
                type="button"
                :aria-expanded="isOpen"
                class="flex items-center justify-between gap-2 w-full h-11 px-4 text-left"
                @click="toggle"
            >
                <slot name="title" />
                <i
                    class="fa-solid fa-chevron-down text-xs transition-transform duration-200"
                    :class="{ 'rotate-180': isOpen }"
                    aria-hidden="true"
                ></i>
            </button>
            <div
                class="overflow-hidden transition-[height,opacity]"
                :class="isOpen ? 'duration-300 transition-ease-out-quad' : 'duration-500 transition-ease-in-out-quad'"
                :style="{ height: contentHeight, opacity: isOpen ? 1 : 0 }"
            >
                <div
                    :ref="(el) => (contentRef = el as HTMLElement)"
                    class="px-4 pb-4 flex flex-col gap-3"
                >
                    <slot name="body" />
                    <slot name="action" />
                </div>
            </div>
        </div>
    </div>
</template>
